const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
const BASE32_INDEX: Record<string, number> = {};
for (let i = 0; i < BASE32_ALPHABET.length; i++) {
  BASE32_INDEX[BASE32_ALPHABET[i]] = i;
}

/** Decode an RFC 4648 base32 string. Tolerant of lowercase, whitespace, and
 *  missing padding. Throws if any character is outside the alphabet. */
export function base32Decode(input: string): Uint8Array {
  const cleaned = input.replace(/\s+/g, '').replace(/=+$/, '').toUpperCase();
  if (cleaned.length === 0) return new Uint8Array(0);

  const out = new Uint8Array(Math.floor((cleaned.length * 5) / 8));
  let buffer = 0;
  let bits = 0;
  let outIdx = 0;
  for (const ch of cleaned) {
    const v = BASE32_INDEX[ch];
    if (v === undefined) throw new Error(`invalid base32 character: ${ch}`);
    buffer = (buffer << 5) | v;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      out[outIdx++] = (buffer >> bits) & 0xff;
    }
  }
  return out;
}

export type TotpAlgorithm = 'SHA1' | 'SHA256' | 'SHA512';

export interface ParsedTotp {
  secret: Uint8Array;
  period: number;
  digits: number;
  algorithm: TotpAlgorithm;
  issuer?: string;
  label?: string;
}

const URI_PREFIX = 'otpauth://totp/';

export function parseTotp(value: string): ParsedTotp {
  if (!value) throw new Error('TOTP value is empty');
  if (value.startsWith(URI_PREFIX)) return parseOtpauthUri(value);
  return {
    secret: base32Decode(value),
    period: 30,
    digits: 6,
    algorithm: 'SHA1',
  };
}

function parseOtpauthUri(uri: string): ParsedTotp {
  // URL parser handles percent-encoding for us. We pass the raw URI through;
  // the `otpauth:` scheme is opaque, so we hand-split path + query.
  const afterPrefix = uri.slice(URI_PREFIX.length);
  const queryIdx = afterPrefix.indexOf('?');
  const path = queryIdx >= 0 ? afterPrefix.slice(0, queryIdx) : afterPrefix;
  const query = queryIdx >= 0 ? afterPrefix.slice(queryIdx + 1) : '';

  const params = new URLSearchParams(query);
  const secretRaw = params.get('secret');
  if (!secretRaw) throw new Error('otpauth URI missing required `secret` parameter');

  const periodStr = params.get('period');
  const digitsStr = params.get('digits');
  const algoStr = (params.get('algorithm') ?? 'SHA1').toUpperCase();

  const period = periodStr === null ? 30 : Number(periodStr);
  if (!Number.isFinite(period) || period <= 0) {
    throw new Error(`otpauth URI has invalid \`period\`: ${periodStr}`);
  }
  const digits = digitsStr === null ? 6 : Number(digitsStr);
  if (!Number.isInteger(digits) || digits < 6 || digits > 8) {
    throw new Error(`otpauth URI has invalid \`digits\`: ${digitsStr} (must be 6, 7, or 8)`);
  }
  if (algoStr !== 'SHA1' && algoStr !== 'SHA256' && algoStr !== 'SHA512') {
    throw new Error(`otpauth URI has unsupported \`algorithm\`: ${algoStr}`);
  }

  // Path is "<label>" OR "<issuer>:<label>" (per the Google Authenticator key URI format).
  const decodedPath = decodeURIComponent(path);
  let pathIssuer: string | undefined;
  let label: string | undefined;
  const colonIdx = decodedPath.indexOf(':');
  if (colonIdx >= 0) {
    pathIssuer = decodedPath.slice(0, colonIdx);
    label = decodedPath.slice(colonIdx + 1);
  } else if (decodedPath) {
    label = decodedPath;
  }
  const issuer = params.get('issuer') ?? pathIssuer;

  return {
    secret: base32Decode(secretRaw),
    period,
    digits,
    algorithm: algoStr as TotpAlgorithm,
    issuer: issuer || undefined,
    label,
  };
}

export interface GeneratedCode {
  code: string;
  remainingMs: number;
}

const WEB_CRYPTO_HASH: Record<TotpAlgorithm, string> = {
  SHA1: 'SHA-1',
  SHA256: 'SHA-256',
  SHA512: 'SHA-512',
};

export async function generateCode(parsed: ParsedTotp, nowMs: number = Date.now()): Promise<GeneratedCode> {
  const periodMs = parsed.period * 1000;
  const counter = Math.floor(nowMs / periodMs);
  const remainingMs = periodMs - (nowMs - counter * periodMs);

  // RFC 6238: 8-byte big-endian counter
  const counterBytes = new Uint8Array(8);
  // Counter is a Unix-time step (<= ~10^9 even far in the future) — fits in 53-bit
  // safe-integer math. The high 4 bytes are zero for any realistic timestamp.
  const counterLow = counter >>> 0;          // low 32 bits
  const counterHigh = Math.floor(counter / 0x100000000) >>> 0;  // high 32 bits
  counterBytes[0] = (counterHigh >> 24) & 0xff;
  counterBytes[1] = (counterHigh >> 16) & 0xff;
  counterBytes[2] = (counterHigh >> 8) & 0xff;
  counterBytes[3] = counterHigh & 0xff;
  counterBytes[4] = (counterLow >> 24) & 0xff;
  counterBytes[5] = (counterLow >> 16) & 0xff;
  counterBytes[6] = (counterLow >> 8) & 0xff;
  counterBytes[7] = counterLow & 0xff;

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    parsed.secret.buffer as ArrayBuffer,
    { name: 'HMAC', hash: WEB_CRYPTO_HASH[parsed.algorithm] },
    false,
    ['sign'],
  );
  const sigBuffer = await crypto.subtle.sign('HMAC', cryptoKey, counterBytes);
  const sig = new Uint8Array(sigBuffer);

  // RFC 6238 dynamic truncation: low nibble of the last byte gives the offset
  const offset = sig[sig.length - 1] & 0x0f;
  const truncated =
    ((sig[offset] & 0x7f) << 24) |
    ((sig[offset + 1] & 0xff) << 16) |
    ((sig[offset + 2] & 0xff) << 8) |
    (sig[offset + 3] & 0xff);

  const modulus = 10 ** parsed.digits;
  const code = String(truncated % modulus).padStart(parsed.digits, '0');

  return { code, remainingMs };
}
